/**
 * App — корневой компонент admin-app.
 *
 * Структура доступа:
 *   - публичные маршруты: /login, /2fa, /recovery
 *   - защищённые: всё остальное, обёрнуто в RequireAuth → PlatformAdminGuard →
 *     AppShell. RequireAuth уводит неаутентифицированных на /login;
 *     PlatformAdminGuard показывает «Доступ ограничен» для не-platform_admin.
 */
import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { AuthProvider, useAuth } from '@hsc/auth';
import { setAuthTokenProvider, setTenantSlugProvider } from '@hsc/api-client';
import { queryClient } from './lib/queryClient.js';
import { ACCESS_TOKEN_KEY, readAccessToken } from './lib/storage.js';
import { PlatformAdminGuard } from './shared/PlatformAdminGuard.js';
import { AppShell } from './shared/AppShell.js';
import { PROTECTED_ROUTES } from './routes/index.js';
import { LoginPage } from './features/auth/LoginPage.js';
import { TwoFactorPage } from './features/auth/TwoFactorPage.js';
import { RecoveryPage } from './features/auth/RecoveryPage.js';

/**
 * Мост токена: подключает `@hsc/api-client.setAuthTokenProvider` к JWT из
 * AuthProvider. admin-app — cross-tenant, поэтому X-Tenant-Slug НЕ выставляем
 * (cross-tenant запросы идут под platform_admin без привязки к одному отелю).
 */
function ApiTokenBridge(): null {
  const { token } = useAuth();
  useEffect(() => {
    setAuthTokenProvider(() => token ?? readAccessToken());
    setTenantSlugProvider(() => null);
  }, [token]);
  return null;
}

function RequireAuth({ children }: { children: JSX.Element }): JSX.Element {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const hasToken = isAuthenticated || Boolean(readAccessToken());
  if (!hasToken) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return children;
}

/** Все защищённые маршруты внутри общего каркаса + гейта platform_admin. */
function ProtectedRoutes(): JSX.Element {
  return (
    <>
      {PROTECTED_ROUTES.map(({ path, Element }) => (
        <Route
          key={path}
          path={path}
          element={
            <RequireAuth>
              <PlatformAdminGuard>
                <AppShell>
                  <Element />
                </AppShell>
              </PlatformAdminGuard>
            </RequireAuth>
          }
        />
      ))}
    </>
  );
}

export function App(): JSX.Element {
  const initialToken = useMemo(() => readAccessToken(), []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider storageKey={ACCESS_TOKEN_KEY} initialToken={initialToken}>
        <ApiTokenBridge />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/2fa" element={<TwoFactorPage />} />
            <Route path="/recovery" element={<RecoveryPage />} />
            {ProtectedRoutes()}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
